// src/components/ui/Search/Search.tsx
import React, {useState, useRef, useCallback, useEffect} from 'react';
import {IonIcon} from '@ionic/react';
import {searchOutline, closeCircleOutline, chevronDownOutline} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Search visual variants */
export type SearchVariant =
    | 'default'      // Full search bar with visible input
    | 'minimal'      // Simplified, clean look
    | 'filled'       // Filled background
    | 'outlined';    // Border emphasis

/** Search sizes */
export type SearchSize = 'sm' | 'md' | 'lg';

/** Search mode */
export type SearchMode =
    | 'standard'     // Always visible input
    | 'expandable'   // Icon that expands to input on click
    | 'icon-only';   // Persistent icon button (triggers callback)

/** Expand direction for expandable mode */
export type ExpandDirection = 'left' | 'right';

/** Search context chip */
export interface SearchContext {
    /** Unique identifier */
    id: string;
    /** Display label */
    label: string;
    /** Optional icon */
    icon?: string;
    /** Optional color accent */
    color?: string;
}

/** Action button for search */
export interface SearchAction {
    /** Icon (ionicons string) */
    icon: string;
    /** Click handler */
    onClick: () => void;
    /** Tooltip text */
    tooltip?: string;
    /** Aria label */
    ariaLabel: string;
    /** Show badge count */
    badge?: number;
}

export interface SearchProps {
    /** Current search value */
    value?: string;
    /** Change handler */
    onChange?: (value: string) => void;
    /** Submit handler (Enter key or search button) */
    onSearch?: (value: string) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Visual variant */
    variant?: SearchVariant;
    /** Size preset */
    size?: SearchSize;
    /** Search mode */
    mode?: SearchMode;
    /** Icon-only click handler (for mode='icon-only') */
    onIconClick?: () => void;
    /** Show clear button when value exists */
    clearable?: boolean;
    /** Disabled state */
    disabled?: boolean;
    /** Loading state */
    loading?: boolean;
    /** Error state */
    error?: boolean;

    // ─── Context & Scope ───────────────────────────────────────────────

    /** Active search context (shows as chip) */
    context?: SearchContext;
    /** Available contexts to choose from */
    contextOptions?: SearchContext[];
    /** Context change handler */
    onContextChange?: (context: SearchContext | null) => void;
    /** Show context dropdown toggle */
    showContextDropdown?: boolean;

    // ─── Addons ───────────────────────────────────────────────────────

    /** Leading action (before input) */
    leadingAction?: SearchAction;
    /** Trailing actions (after input, max 2) */
    trailingActions?: SearchAction[];

    // ─── Expandable Mode ───────────────────────────────────────────────

    /** Direction to expand (left or right) */
    expandDirection?: ExpandDirection;
    /** Width when expanded (px or string like '300px', '100%') */
    expandedWidth?: number | string;
    /** Initial expanded state (for expandable mode) */
    defaultExpanded?: boolean;
    /** Controlled expanded state */
    expanded?: boolean;
    /** Expand/collapse change handler */
    onExpandedChange?: (expanded: boolean) => void;
    /** Collapse on blur (for expandable mode) */
    collapseOnBlur?: boolean;

    // ─── Mobile Experience ───────────────────────────────────────────

    /** Enable mobile-optimized fullscreen mode */
    mobileFullscreen?: boolean;
    /** Callback when mobile fullscreen opens */
    onMobileOpen?: () => void;
    /** Callback when mobile fullscreen closes */
    onMobileClose?: () => void;

    // ─── Misc ───────────────────────────────────────────────────────

    /** Auto-focus on mount */
    autoFocus?: boolean;
    /** Input id */
    id?: string;
    /** Aria label */
    'aria-label'?: string;
    /** Additional className */
    className?: string;
    /** Inline styles */
    style?: React.CSSProperties;
}

// ═══════════════════════════════════════════════════════════════════
// SIZE PRESETS
// ═══════════════════════════════════════════════════════════════════

interface SizePreset {
    height: number;
    fontSize: number;
    paddingX: number;
    iconSize: number;
    gap: number;
    iconButtonSize: number;
    iconPadding: number; // Space around icons at edges
}

const sizePresets: Record<SearchSize, SizePreset> = {
    sm: {
        height: 36,
        fontSize: 14,
        paddingX: 12,
        iconSize: 18,
        gap: 10,
        iconButtonSize: 32,
        iconPadding: 10,
    },
    md: {
        height: 44,
        fontSize: 15,
        paddingX: 14,
        iconSize: 20,
        gap: 12,
        iconButtonSize: 40,
        iconPadding: 12,
    },
    lg: {
        height: 52,
        fontSize: 17,
        paddingX: 16,
        iconSize: 22,
        gap: 14,
        iconButtonSize: 48,
        iconPadding: 14,
    },
};

// ═══════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════

/** Loading spinner - Forge-inspired pulse animation */
const ForgeSpinner: React.FC<{size: number}> = ({size}) => (
    <div style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }}>
        <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            border: '2px solid var(--ion-color-step-200)',
            borderTopColor: 'var(--kff-forge-gold)',
            borderRadius: '50%',
            animation: 'forge-spin 0.8s linear infinite',
        }} />
        <style>{`
            @keyframes forge-spin {
                to { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

/** Context chip badge */
const ContextChip: React.FC<{
    context: SearchContext;
    onRemove?: () => void;
    showDropdown?: boolean;
    onClick?: () => void;
    size: SizePreset;
}> = ({context, onRemove, showDropdown, onClick, size}) => (
    <div
        onClick={onClick}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            background: context.color || 'var(--kff-foundry-blue)',
            borderRadius: 2,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.15s ease',
            flexShrink: 0,
        }}
    >
        {context.icon && (
            <IonIcon icon={context.icon} style={{fontSize: size.iconSize - 4, color: 'rgba(255,255,255,0.9)'}} />
        )}
        <span style={{
            fontFamily: 'var(--kff-data-font)',
            fontSize: size.fontSize - 2,
            fontWeight: 500,
            color: '#ffffff',
            whiteSpace: 'nowrap',
        }}>
            {context.label}
        </span>
        {showDropdown && (
            <IonIcon icon={chevronDownOutline} style={{fontSize: 12, color: 'rgba(255,255,255,0.7)'}} />
        )}
        {onRemove && (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    marginLeft: 2,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(255,255,255,0.7)',
                    transition: 'color 0.15s',
                }}
                onMouseEnter={e => {e.currentTarget.style.color = '#ffffff';}}
                onMouseLeave={e => {e.currentTarget.style.color = 'rgba(255,255,255,0.7)';}}
                aria-label={`Remove ${context.label} filter`}
            >
                <IonIcon icon={closeCircleOutline} style={{fontSize: 14}} />
            </button>
        )}
    </div>
);

/** Action button with optional badge */
const ActionButton: React.FC<{
    action: SearchAction;
    size: SizePreset;
}> = ({action, size}) => {
    const [btnHovered, setBtnHovered] = useState(false);

    return (
        <button
            onClick={action.onClick}
            title={action.tooltip}
            aria-label={action.ariaLabel}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size.iconButtonSize - 8,
                height: size.iconButtonSize - 8,
                padding: 0,
                border: 'none',
                borderRadius: 2,
                background: btnHovered ? 'var(--ion-color-step-100)' : 'transparent',
                cursor: 'pointer',
                color: btnHovered ? 'var(--kff-forge-gold)' : 'var(--ion-color-medium)',
                transition: 'all 0.15s ease',
            }}
        >
            <IonIcon icon={action.icon} style={{fontSize: size.iconSize}} />
            {action.badge !== undefined && action.badge > 0 && (
                <span style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: 'var(--ion-color-danger)',
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {action.badge > 99 ? '99+' : action.badge}
                </span>
            )}
        </button>
    );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Search: React.FC<SearchProps> = ({
    value = '',
    onChange,
    onSearch,
    placeholder = 'Search...',
    variant = 'default',
    size = 'md',
    mode = 'standard',
    onIconClick,
    clearable = true,
    disabled = false,
    loading = false,
    error = false,
    // Context
    context,
    contextOptions,
    onContextChange,
    showContextDropdown = false,
    // Addons
    leadingAction,
    trailingActions = [],
    // Expandable
    expandDirection = 'left',
    expandedWidth = 280,
    defaultExpanded = false,
    expanded: controlledExpanded,
    onExpandedChange,
    collapseOnBlur = true,
    // Mobile
    mobileFullscreen = false,
    onMobileOpen,
    onMobileClose,
    // Misc
    autoFocus = false,
    id,
    'aria-label': ariaLabel,
    className,
    style,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const [showMobileFullscreen, setShowMobileFullscreen] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState(false);

    const sizes = sizePresets[size];
    const hasValue = value.length > 0;
    const showClearButton = clearable && hasValue && !disabled && mode !== 'icon-only';

    // Handle controlled vs uncontrolled expanded state
    const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
    const setExpanded = (exp: boolean) => {
        setInternalExpanded(exp);
        onExpandedChange?.(exp);
    };

    // Auto-focus when expanded
    useEffect(() => {
        if (isExpanded && mode === 'expandable') {
            // Small delay to let the animation start
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isExpanded, mode]);

    // Auto-focus on mount
    useEffect(() => {
        if (autoFocus && mode !== 'icon-only') {
            inputRef.current?.focus();
        }
    }, [autoFocus, mode]);

    // Handle focus
    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    // Handle blur
    const handleBlur = useCallback(() => {
        setIsFocused(false);
        if (mode === 'expandable' && collapseOnBlur && !hasValue) {
            setTimeout(() => setExpanded(false), 200);
        }
    }, [mode, collapseOnBlur, hasValue]);

    // Handle input change
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(e.target.value);
    }, [onChange]);

    // Handle clear
    const handleClear = useCallback(() => {
        onChange?.('');
        inputRef.current?.focus();
    }, [onChange]);

    // Handle key events
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSearch?.(value);
        }
        if (e.key === 'Escape') {
            if (mode === 'expandable') {
                setExpanded(false);
                inputRef.current?.blur();
            }
            if (showMobileFullscreen) {
                setShowMobileFullscreen(false);
                onMobileClose?.();
            }
        }
    }, [value, onSearch, mode, showMobileFullscreen, onMobileClose]);

    // Handle icon click (expandable or icon-only mode)
    const handleIconClick = useCallback(() => {
        if (mode === 'icon-only') {
            onIconClick?.();
        } else if (mode === 'expandable') {
            setExpanded(true);
        }
    }, [mode, onIconClick]);

    // Handle mobile fullscreen open
    const handleMobileOpen = useCallback(() => {
        setShowMobileFullscreen(true);
        onMobileOpen?.();
    }, [onMobileOpen]);

    // ─── Variant Styles ───────────────────────────────────────────────

    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: sizes.gap,
            height: sizes.height,
            padding: `0 ${sizes.iconPadding}px`,
            borderRadius: 0, // No border radius - industrial aesthetic
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.5 : 1,
            position: 'relative',
            overflow: 'hidden',
        };

        const borderColor = error
            ? 'var(--ion-color-danger)'
            : isFocused
                ? 'var(--kff-forge-gold)'
                : isHovered
                    ? 'var(--ion-color-step-400)'
                    : 'var(--ion-color-step-200)';

        switch (variant) {
            case 'default':
                return {
                    ...base,
                    background: 'var(--ion-card-background)',
                    border: `1.5px solid ${borderColor}`,
                    boxShadow: isFocused
                        ? '0 0 0 3px rgba(203, 160, 82, 0.15), 0 4px 16px rgba(0,0,0,0.1)'
                        : isHovered
                            ? '0 2px 8px rgba(0,0,0,0.08)'
                            : 'none',
                };
            case 'minimal':
                return {
                    ...base,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1.5px solid ${isHovered ? 'var(--ion-color-step-400)' : 'var(--ion-color-step-150)'}`,
                    paddingLeft: sizes.iconPadding,
                    paddingRight: sizes.iconPadding,
                    // Note: Gold underline bar is rendered separately for animation
                };
            case 'filled':
                return {
                    ...base,
                    background: isFocused
                        ? 'var(--ion-color-step-50)'
                        : 'var(--ion-color-step-100)',
                    border: `1.5px solid ${isFocused ? borderColor : 'transparent'}`,
                    boxShadow: isFocused ? '0 0 0 3px rgba(203, 160, 82, 0.1)' : undefined,
                };
            case 'outlined':
                return {
                    ...base,
                    background: 'transparent',
                    border: `2px solid ${borderColor}`,
                    boxShadow: isFocused ? '0 0 0 3px rgba(203, 160, 82, 0.12)' : 'none',
                };
            default:
                return base;
        }
    };

    const inputStyles: React.CSSProperties = {
        flex: 1,
        width: '100%',
        minWidth: 0,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        fontFamily: 'var(--kff-data-font)',
        fontSize: sizes.fontSize,
        fontWeight: 500,
        color: 'var(--ion-text-color)',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'text',
    };

    // ─── Icon-Only Mode ───────────────────────────────────────────────

    if (mode === 'icon-only') {
        return (
            <button
                onClick={handleIconClick}
                disabled={disabled}
                aria-label={ariaLabel || 'Search'}
                className={className}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: sizes.iconButtonSize,
                    height: sizes.iconButtonSize,
                    padding: 0,
                    border: 'none',
                    borderRadius: 0,
                    background: isHovered
                        ? 'var(--ion-color-step-100)'
                        : 'transparent',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    color: isHovered ? 'var(--kff-forge-gold)' : 'var(--ion-color-medium)',
                    transition: 'all 0.2s ease',
                    opacity: disabled ? 0.5 : 1,
                    ...style,
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <IonIcon icon={searchOutline} style={{fontSize: sizes.iconSize}} />
            </button>
        );
    }

    // ─── Expandable Mode ───────────────────────────────────────────────

    if (mode === 'expandable') {
        const expandedWidthValue = typeof expandedWidth === 'number' ? `${expandedWidth}px` : expandedWidth;
        const isLeft = expandDirection === 'left';

        // Get expandable-specific styles with tighter padding
        const getExpandableStyles = (): React.CSSProperties => {
            const baseStyles = getContainerStyles();
            return {
                ...baseStyles,
                paddingLeft: 4,
                paddingRight: 4,
            };
        };

        return (
            <div
                ref={containerRef}
                className={className}
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: isLeft ? 'flex-end' : 'flex-start',
                    ...style,
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Sliding Search Bar */}
                <div
                    style={{
                        position: 'absolute',
                        [isLeft ? 'right' : 'left']: 0,
                        top: 0,
                        height: '100%',
                        width: isExpanded ? expandedWidthValue : sizes.iconButtonSize,
                        overflow: 'hidden',
                        transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isLeft ? 'flex-end' : 'flex-start',
                    }}
                >
                    <div
                        style={{
                            ...getExpandableStyles(),
                            width: '100%',
                            minWidth: expandedWidthValue,
                            opacity: isExpanded ? 1 : 0,
                            transform: isExpanded
                                ? 'translateX(0)'
                                : `translateX(${isLeft ? '20px' : '-20px'})`,
                            transition: 'opacity 0.25s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s ease',
                            transitionDelay: isExpanded ? '0.1s' : '0s',
                        }}
                    >
                        {/* Search Icon - always on left */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: isFocused
                                ? 'var(--kff-forge-gold)'
                                : 'var(--ion-color-medium)',
                            transition: 'color 0.2s ease',
                            paddingLeft: 0,
                            paddingRight: 2,
                        }}>
                            {loading ? (
                                <ForgeSpinner size={sizes.iconSize} />
                            ) : (
                                <IonIcon icon={searchOutline} style={{fontSize: sizes.iconSize}} />
                            )}
                        </div>

                        {/* Context Chip */}
                        {context && (
                            <ContextChip
                                context={context}
                                onRemove={onContextChange ? () => onContextChange(null) : undefined}
                                showDropdown={showContextDropdown && contextOptions && contextOptions.length > 0}
                                onClick={showContextDropdown ? () => setShowContextMenu(!showContextMenu) : undefined}
                                size={sizes}
                            />
                        )}

                        {/* Input */}
                        <input
                            ref={inputRef}
                            type="text"
                            value={value}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={context ? `in ${context.label}...` : placeholder}
                            disabled={disabled}
                            id={id}
                            aria-label={ariaLabel || 'Search'}
                            style={inputStyles}
                            tabIndex={isExpanded ? 0 : -1}
                        />

                        {/* Trailing Actions */}
                        {trailingActions.slice(0, 2).map((action, idx) => (
                            <ActionButton key={idx} action={action} size={sizes} />
                        ))}

                        {/* Clear Button - always on right */}
                        {showClearButton && isExpanded && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClear();
                                }}
                                aria-label="Clear search"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    width: sizes.iconButtonSize - 12,
                                    height: sizes.iconButtonSize - 12,
                                    padding: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--ion-color-medium)',
                                    cursor: 'pointer',
                                    transition: 'color 0.15s',
                                    borderRadius: 2,
                                    marginRight: 0,
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.color = 'var(--ion-color-danger)';
                                    e.currentTarget.style.background = 'var(--ion-color-step-100)';
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.color = 'var(--ion-color-medium)';
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <IonIcon icon={closeCircleOutline} style={{fontSize: sizes.iconSize - 2}} />
                            </button>
                        )}

                        {/* Animated Gold Underline Bar (minimal variant only) */}
                        {variant === 'minimal' && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 2,
                                    background: 'var(--kff-forge-gold)',
                                    transform: isExpanded ? 'scaleX(1)' : 'scaleX(0)',
                                    transformOrigin: 'center',
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transitionDelay: isExpanded ? '0.1s' : '0s',
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Trigger Icon Button (always visible, positioned based on direction) */}
                <button
                    onClick={handleIconClick}
                    disabled={disabled}
                    aria-label={ariaLabel || 'Expand search'}
                    style={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: sizes.iconButtonSize,
                        height: sizes.iconButtonSize,
                        padding: 0,
                        border: variant === 'outlined' ? '2px solid var(--ion-color-step-200)' : 'none',
                        borderRadius: 0,
                        background: variant === 'filled'
                            ? 'var(--ion-color-step-100)'
                            : isHovered && !isExpanded
                                ? 'var(--ion-color-step-100)'
                                : 'transparent',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        color: isHovered || isExpanded ? 'var(--kff-forge-gold)' : 'var(--ion-color-medium)',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: isExpanded ? 0 : 1,
                        pointerEvents: isExpanded ? 'none' : 'auto',
                        transform: isExpanded ? 'scale(0.8)' : 'scale(1)',
                    }}
                >
                    <IonIcon icon={searchOutline} style={{fontSize: sizes.iconSize}} />
                </button>

                {/* Reserve space for expanded state */}
                <div style={{
                    width: isExpanded ? expandedWidthValue : sizes.iconButtonSize,
                    height: sizes.iconButtonSize,
                    transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: 'none',
                }} />
            </div>
        );
    }

    // ─── Mobile Fullscreen Overlay ───────────────────────────────────

    if (showMobileFullscreen && mobileFullscreen) {
        return (
            <>
                {/* Trigger button for mobile */}
                <div className={className} style={style}>
                    <button
                        onClick={handleMobileOpen}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: sizes.gap,
                            width: '100%',
                            height: sizes.height,
                            padding: `0 ${sizes.iconPadding}px`,
                            borderRadius: 0,
                            border: variant === 'outlined'
                                ? '2px solid var(--ion-color-step-200)'
                                : '1.5px solid var(--ion-color-step-200)',
                            background: variant === 'filled'
                                ? 'var(--ion-color-step-100)'
                                : 'var(--ion-card-background)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <IonIcon icon={searchOutline} style={{fontSize: sizes.iconSize, color: 'var(--ion-color-medium)'}} />
                        <span style={{
                            flex: 1,
                            textAlign: 'left',
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: sizes.fontSize,
                            color: 'var(--ion-color-medium)',
                        }}>
                            {placeholder}
                        </span>
                    </button>
                </div>

                {/* Fullscreen overlay */}
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 10000,
                    background: 'var(--ion-background-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideUp 0.25s ease',
                }}>
                    <style>{`
                        @keyframes slideUp {
                            from { transform: translateY(100%); opacity: 0; }
                            to { transform: translateY(0); opacity: 1; }
                        }
                    `}</style>

                    {/* Mobile search header */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                        background: 'var(--ion-card-background)',
                        borderBottom: '1px solid var(--ion-border-color)',
                    }}>
                        <button
                            onClick={() => {
                                setShowMobileFullscreen(false);
                                onMobileClose?.();
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                padding: 0,
                                border: 'none',
                                borderRadius: 0,
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'var(--ion-text-color)',
                            }}
                            aria-label="Close search"
                        >
                            <IonIcon icon={closeCircleOutline} style={{fontSize: 24}} />
                        </button>

                        <div style={{flex: 1, position: 'relative'}}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={value}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                placeholder={placeholder}
                                autoFocus
                                style={{
                                    width: '100%',
                                    height: 44,
                                    padding: '0 12px',
                                    paddingLeft: 44,
                                    border: '1.5px solid var(--kff-forge-gold)',
                                    borderRadius: 0,
                                    background: 'var(--ion-color-step-50)',
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: 16,
                                    color: 'var(--ion-text-color)',
                                    outline: 'none',
                                }}
                            />
                            <IonIcon
                                icon={searchOutline}
                                style={{
                                    position: 'absolute',
                                    left: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    fontSize: 20,
                                    color: 'var(--kff-forge-gold)',
                                }}
                            />
                            {showClearButton && (
                                <button
                                    onClick={handleClear}
                                    style={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 28,
                                        height: 28,
                                        padding: 0,
                                        border: 'none',
                                        borderRadius: 0,
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: 'var(--ion-color-medium)',
                                    }}
                                    aria-label="Clear search"
                                >
                                    <IonIcon icon={closeCircleOutline} style={{fontSize: 20}} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Context options */}
                    {contextOptions && contextOptions.length > 0 && (
                        <div style={{
                            padding: '16px',
                            borderBottom: '1px solid var(--ion-border-color)',
                        }}>
                            <div style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'var(--ion-color-medium)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                marginBottom: 12,
                            }}>
                                Search In
                            </div>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                                {contextOptions.map(opt => (
                                    <button
                                        key={opt.id}
                                        onClick={() => {
                                            onContextChange?.(context?.id === opt.id ? null : opt);
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '8px 14px',
                                            border: context?.id === opt.id
                                                ? '2px solid var(--kff-forge-gold)'
                                                : '1px solid var(--ion-border-color)',
                                            borderRadius: 0,
                                            background: context?.id === opt.id
                                                ? 'var(--ion-color-step-100)'
                                                : 'transparent',
                                            cursor: 'pointer',
                                            fontFamily: 'var(--kff-data-font)',
                                            fontSize: 14,
                                            color: 'var(--ion-text-color)',
                                        }}
                                    >
                                        {opt.icon && <IonIcon icon={opt.icon} style={{fontSize: 18}} />}
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Results area placeholder */}
                    <div style={{flex: 1, padding: 16}}>
                        {hasValue && (
                            <div style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 14,
                                color: 'var(--ion-color-medium)',
                            }}>
                                Searching for "{value}"...
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // ─── Standard Mode ───────────────────────────────────────────────

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                ...getContainerStyles(),
                ...style,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                if (!disabled) {
                    inputRef.current?.focus();
                }
            }}
        >
            {/* Leading Action */}
            {leadingAction && (
                <div style={{paddingLeft: 2}}>
                    <ActionButton action={leadingAction} size={sizes} />
                </div>
            )}

            {/* Search Icon */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: isFocused
                    ? 'var(--kff-forge-gold)'
                    : 'var(--ion-color-medium)',
                transition: 'color 0.2s ease',
                paddingLeft: leadingAction ? 0 : 2,
                paddingRight: 2,
            }}>
                {loading ? (
                    <ForgeSpinner size={sizes.iconSize} />
                ) : (
                    <IonIcon icon={searchOutline} style={{fontSize: sizes.iconSize}} />
                )}
            </div>

            {/* Context Chip */}
            {context && (
                <ContextChip
                    context={context}
                    onRemove={onContextChange ? () => onContextChange(null) : undefined}
                    showDropdown={showContextDropdown && contextOptions && contextOptions.length > 0}
                    onClick={showContextDropdown ? () => setShowContextMenu(!showContextMenu) : undefined}
                    size={sizes}
                />
            )}

            {/* Input */}
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={context ? `in ${context.label}...` : placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                id={id}
                aria-label={ariaLabel || 'Search'}
                style={inputStyles}
            />

            {/* Clear Button */}
            {showClearButton && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClear();
                    }}
                    aria-label="Clear search"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: sizes.iconButtonSize - 12,
                        height: sizes.iconButtonSize - 12,
                        padding: 0,
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ion-color-medium)',
                        cursor: 'pointer',
                        opacity: isHovered || isFocused || hasValue ? 1 : 0,
                        transition: 'opacity 0.15s, color 0.15s, background 0.15s',
                        borderRadius: 2,
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.color = 'var(--ion-color-danger)';
                        e.currentTarget.style.background = 'var(--ion-color-step-100)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.color = 'var(--ion-color-medium)';
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <IonIcon icon={closeCircleOutline} style={{fontSize: sizes.iconSize - 2}} />
                </button>
            )}

            {/* Trailing Actions */}
            {trailingActions.slice(0, 2).map((action, idx) => (
                <div key={idx} style={{paddingRight: idx === trailingActions.length - 1 ? 2 : 0}}>
                    <ActionButton action={action} size={sizes} />
                </div>
            ))}

            {/* Animated Gold Underline Bar (minimal variant only) */}
            {variant === 'minimal' && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 2,
                        background: 'var(--kff-forge-gold)',
                        transform: isFocused ? 'scaleX(1)' : 'scaleX(0)',
                        transformOrigin: 'center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            )}

            {/* Context Dropdown Menu */}
            {showContextMenu && contextOptions && contextOptions.length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        background: 'var(--ion-card-background)',
                        border: '1px solid var(--ion-border-color)',
                        borderRadius: 0,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        overflow: 'hidden',
                        zIndex: 100,
                        animation: 'fadeIn 0.15s ease',
                    }}
                >
                    <style>{`
                        @keyframes fadeIn {
                            from { opacity: 0; transform: translateY(-8px); }
                            to { opacity: 1; transform: translateY(0); }
                        }
                    `}</style>
                    <div style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--ion-color-medium)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        padding: '10px 12px 6px',
                    }}>
                        Search Scope
                    </div>
                    {contextOptions.map(opt => (
                        <button
                            key={opt.id}
                            onClick={() => {
                                onContextChange?.(opt);
                                setShowContextMenu(false);
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: '100%',
                                padding: '10px 12px',
                                border: 'none',
                                background: context?.id === opt.id
                                    ? 'var(--ion-color-step-100)'
                                    : 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left',
                                transition: 'background 0.1s',
                            }}
                            onMouseEnter={e => {
                                if (context?.id !== opt.id) {
                                    e.currentTarget.style.background = 'var(--ion-color-step-50)';
                                }
                            }}
                            onMouseLeave={e => {
                                if (context?.id !== opt.id) {
                                    e.currentTarget.style.background = 'transparent';
                                }
                            }}
                        >
                            {opt.icon && (
                                <IonIcon
                                    icon={opt.icon}
                                    style={{
                                        fontSize: 18,
                                        color: opt.color || 'var(--ion-color-medium)',
                                    }}
                                />
                            )}
                            <span style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 14,
                                color: 'var(--ion-text-color)',
                            }}>
                                {opt.label}
                            </span>
                            {context?.id === opt.id && (
                                <span style={{
                                    marginLeft: 'auto',
                                    fontSize: 12,
                                    color: 'var(--kff-forge-gold)',
                                }}>
                                    Active
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

Search.displayName = 'Search';

export default Search;
